/**
 * @fileoverview Painel de seleção por filtro dinâmico para o node bulk_message.
 * Pega um `filter` do nodeData e mostra inputs + preview live.
 */

import React, { useMemo } from "react";
import { Tooltip, CircularProgress } from "@material-ui/core";
import { Info, Users } from "lucide-react";
import useContactPreview from "../../../../hooks/useContactPreview";

const fieldWrapStyle = { marginBottom: 10 };
const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.7)",
  marginBottom: 4,
  display: "block",
};
const inputStyle = {
  width: "100%",
  padding: "7px 10px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.03)",
  color: "rgba(255,255,255,0.9)",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
};
const switchRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 0",
};
const switchLabelStyle = {
  fontSize: 11,
  color: "rgba(255,255,255,0.7)",
  flex: 1,
  paddingRight: 8,
};

const BulkFilterPanel = ({ filter, onFilterChange, templateName, classes }) => {
  // normaliza: se algum campo for "", devolve undefined pra não virar filtro vazio
  const normalized = useMemo(() => {
    const f = filter || {};
    const out = {};
    Object.keys(f).forEach((k) => {
      const v = f[k];
      if (v === "" || v === null || v === undefined) return;
      out[k] = v;
    });
    // Ignorar grupos: default true quando não especificado
    if (out.excludeGroups === undefined) out.excludeGroups = true;
    return out;
  }, [filter]);

  const hasAnyFilter =
    normalized.nameContains ||
    normalized.nameContains2 ||
    normalized.numberContains ||
    normalized.emailContains ||
    normalized.createdFrom ||
    normalized.createdTo ||
    normalized.lastInteractionFrom ||
    normalized.lastInteractionTo ||
    normalized.excludeReceivedTemplateSince;

  const { total, sample, loading } = useContactPreview(normalized, true);

  const update = (key, value) => {
    const next = { ...(filter || {}) };
    if (value === "" || value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onFilterChange(next);
  };

  const toggleDedup = (on) => {
    if (!on) {
      update("excludeReceivedTemplateSince", undefined);
      return;
    }
    update("excludeReceivedTemplateSince", { days: 30 });
  };

  const dedup = filter?.excludeReceivedTemplateSince;

  return (
    <div>
      <div style={fieldWrapStyle}>
        <label style={labelStyle}>Nome contém</label>
        <input
          style={inputStyle}
          placeholder="Ex: Splash"
          value={filter?.nameContains || ""}
          onChange={(e) => update("nameContains", e.target.value)}
        />
      </div>

      <div style={fieldWrapStyle}>
        <label style={labelStyle}>
          Nome também contém
          <Tooltip
            title="Segundo termo procurado dentro do nome do contato (E, não OU). Útil quando o nome concatena várias informações. Ex: nome 'Cliente Splash Daniel São Pedro' — filtre 'Splash' acima e 'São Pedro' aqui."
            placement="top"
            classes={{ tooltip: classes.configInfoTooltip }}
          >
            <span
              style={{
                marginLeft: 6,
                display: "inline-flex",
                verticalAlign: "middle",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              <Info size={12} />
            </span>
          </Tooltip>
        </label>
        <input
          style={inputStyle}
          placeholder="Ex: São Pedro"
          value={filter?.nameContains2 || ""}
          onChange={(e) => update("nameContains2", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Número contém</label>
          <input
            style={inputStyle}
            placeholder="Ex: 5511"
            value={filter?.numberContains || ""}
            onChange={(e) => update("numberContains", e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Email contém</label>
          <input
            style={inputStyle}
            placeholder="@exemplo.com"
            value={filter?.emailContains || ""}
            onChange={(e) => update("emailContains", e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Criado de</label>
          <input
            style={inputStyle}
            type="date"
            value={filter?.createdFrom || ""}
            onChange={(e) => update("createdFrom", e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Até</label>
          <input
            style={inputStyle}
            type="date"
            value={filter?.createdTo || ""}
            onChange={(e) => update("createdTo", e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Última interação de</label>
          <input
            style={inputStyle}
            type="date"
            value={filter?.lastInteractionFrom || ""}
            onChange={(e) => update("lastInteractionFrom", e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Até</label>
          <input
            style={inputStyle}
            type="date"
            value={filter?.lastInteractionTo || ""}
            onChange={(e) => update("lastInteractionTo", e.target.value)}
          />
        </div>
      </div>

      <div style={switchRowStyle}>
        <span style={switchLabelStyle}>
          Ignorar grupos
          <Tooltip
            title="Exclui contatos que representam grupos do WhatsApp."
            placement="top"
            classes={{ tooltip: classes.configInfoTooltip }}
          >
            <span
              style={{
                marginLeft: 6,
                display: "inline-flex",
                verticalAlign: "middle",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              <Info size={12} />
            </span>
          </Tooltip>
        </span>
        <input
          type="checkbox"
          checked={filter?.excludeGroups !== false}
          onChange={(e) => update("excludeGroups", e.target.checked)}
        />
      </div>

      {templateName ? (
        <div style={switchRowStyle}>
          <span style={switchLabelStyle}>
            Não enviar para quem recebeu este template nos últimos
          </span>
          <input
            type="checkbox"
            checked={Boolean(dedup)}
            onChange={(e) => toggleDedup(e.target.checked)}
          />
        </div>
      ) : null}
      {dedup ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <input
            style={{ ...inputStyle, width: 70 }}
            type="number"
            min="1"
            max="365"
            value={dedup.days || 30}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              update("excludeReceivedTemplateSince", {
                ...dedup,
                days: isNaN(n) ? 30 : n,
              });
            }}
          />
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>dias</span>
        </div>
      ) : null}

      <div style={fieldWrapStyle}>
        <label style={labelStyle}>Limite máximo de contatos</label>
        <input
          style={{ ...inputStyle, width: 120 }}
          type="number"
          min="1"
          max="10000"
          placeholder="Sem limite"
          value={filter?.maxContacts || ""}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            update("maxContacts", isNaN(n) ? undefined : n);
          }}
        />
      </div>

      {/* Preview */}
      <div
        style={{
          marginTop: 14,
          padding: "10px 12px",
          borderRadius: 8,
          background: "rgba(99,102,241,0.08)",
          border: "1px solid rgba(99,102,241,0.22)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {loading ? (
            <CircularProgress size={14} style={{ color: "#818cf8" }} />
          ) : (
            <Users size={14} color="#818cf8" />
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: "#a5b4fc" }}>
            {loading
              ? "Calculando..."
              : hasAnyFilter
              ? `${total.toLocaleString("pt-BR")} contato${total === 1 ? "" : "s"} atende${total === 1 ? "" : "m"}`
              : `${total.toLocaleString("pt-BR")} contatos (sem filtro)`}
          </span>
        </div>
        {sample && sample.length > 0 && (
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px dashed rgba(99,102,241,0.2)",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.45)",
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Amostra:
            </div>
            {sample.map((c) => (
              <div
                key={c.id}
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.75)",
                  padding: "2px 0",
                  display: "flex",
                  gap: 8,
                }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.name || "(sem nome)"}
                </span>
                <span
                  style={{
                    fontFamily: "monospace",
                    color: "rgba(255,255,255,0.4)",
                  }}
                >
                  {c.number}
                </span>
              </div>
            ))}
            {total > sample.length && (
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.35)",
                  marginTop: 4,
                  fontStyle: "italic",
                }}
              >
                + {(total - sample.length).toLocaleString("pt-BR")} outros
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkFilterPanel;
